import { OrganizationFragment } from "@graphql/organizations/organizations.g";
import React from "react";

import { Table } from "@design-system/Table/Table";
import { Tag } from "@design-system/Tag/Tag";
import { Text } from "@design-system/Text/Text";

import { createTableConfiguration } from "@utils/table/TableConfigurationSystem";

import {
  DEFAULT_ORGANIZATION_TABLE_COLUMN_ORDER,
  DEFAULT_ORGANIZATION_TABLE_COLUMN_SETTINGS,
  ORGANIZATION_TABLE_COLUMN_LABELS,
  OrganizationTableColumns,
} from "./UI/OrganizationsTableColumns";

/**
 * Default filter values for Organizations
 */
export const DEFAULT_ORGANIZATION_FILTER_VALUES = {
  nameSearch: "",
  organizationTypeFilter: "",
};

/**
 * Comprehensive table configuration for Organizations
 */
export const OrganizationsTableConfig = createTableConfiguration<
  OrganizationFragment,
  OrganizationTableColumns
>({
  tableId: "organizations",
  columns: {
    [OrganizationTableColumns.name]: {
      id: OrganizationTableColumns.name,
      label: ORGANIZATION_TABLE_COLUMN_LABELS[OrganizationTableColumns.name],
      fieldPath: "name",
      sortField: "name",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown, row: OrganizationFragment) => {
        return renderName(value, row);
      },
    },
    [OrganizationTableColumns.organizationType]: {
      id: OrganizationTableColumns.organizationType,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[
          OrganizationTableColumns.organizationType
        ],
      fieldPath: "organizationType",
      sortField: "organization_type",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderOrganizationType(value);
      },
    },
    [OrganizationTableColumns.stytchOrganizationId]: {
      id: OrganizationTableColumns.stytchOrganizationId,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[
          OrganizationTableColumns.stytchOrganizationId
        ],
      fieldPath: "stytchOrganizationId",
      sortField: "stytch_organization_id",
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderStytchOrganizationId(value);
      },
    },
    [OrganizationTableColumns.clientCount]: {
      id: OrganizationTableColumns.clientCount,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[OrganizationTableColumns.clientCount],
      fieldPath: "clientCount",
      sortField: "client_count",
      sortable: false,
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCount(value);
      },
    },
    [OrganizationTableColumns.userCount]: {
      id: OrganizationTableColumns.userCount,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[OrganizationTableColumns.userCount],
      fieldPath: "userCount",
      sortField: "user_count",
      sortable: false,
      type: "custom",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCount(value);
      },
    },
    [OrganizationTableColumns.employeeCount]: {
      id: OrganizationTableColumns.employeeCount,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[
          OrganizationTableColumns.employeeCount
        ],
      fieldPath: "employeeCount",
      sortField: "employee_count",
      sortable: false,
      type: "custom",
      defaultVisible: false,
      render: (value: unknown) => {
        return renderCount(value);
      },
    },
    [OrganizationTableColumns.createdAt]: {
      id: OrganizationTableColumns.createdAt,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[OrganizationTableColumns.createdAt],
      fieldPath: "createdAt",
      sortField: "created_at",
      type: "date",
      defaultVisible: true,
      render: (value: unknown) => {
        return renderCreatedAt(value);
      },
    },
    [OrganizationTableColumns.updatedAt]: {
      id: OrganizationTableColumns.updatedAt,
      label:
        ORGANIZATION_TABLE_COLUMN_LABELS[OrganizationTableColumns.updatedAt],
      fieldPath: "updatedAt",
      sortField: "updated_at",
      type: "date",
      defaultVisible: false,
      render: (value: unknown) => {
        return renderUpdatedAt(value);
      },
    },
  },
  defaultColumnOrder: DEFAULT_ORGANIZATION_TABLE_COLUMN_ORDER,
  defaultColumnSettings: DEFAULT_ORGANIZATION_TABLE_COLUMN_SETTINGS,
});

/**
 * Helper function to get default filter values
 */
export const getDefaultFilterValues = () => ({
  ...DEFAULT_ORGANIZATION_FILTER_VALUES,
});

/**
 * Helper function to check if filters are active
 */
export const areFiltersActive = (filterValues: {
  nameSearch: string;
  organizationTypeFilter: string;
}): boolean => {
  return Boolean(
    (filterValues.nameSearch && filterValues.nameSearch.trim()) ||
      (filterValues.organizationTypeFilter &&
        filterValues.organizationTypeFilter.trim()),
  );
};

/**
 * Custom render functions for specific columns
 */
export function renderName(
  value: unknown,
  row: OrganizationFragment,
): React.ReactNode {
  return (
    <div id={`organization-name-${row.stytchOrganizationId}`}>
      <Text weight={500}>{String(value)}</Text>
    </div>
  );
}

export function renderOrganizationType(value: unknown): React.ReactNode {
  const orgType = String(value);

  // Color coding for different organization types
  const getTypeColor = (type: string) => {
    switch (type) {
      case "MSP":
        return "primary";
      case "DISTRIBUTOR":
        return "secondary";
      default:
        return "tertiary";
    }
  };

  return <Tag text={orgType} color={getTypeColor(orgType)} variant="solid" />;
}

export function renderStytchOrganizationId(value: unknown): React.ReactNode {
  if (!value) {
    return <Text color="textSecondary">—</Text>;
  }
  return <Text truncate>{String(value)}</Text>;
}

export function renderCount(value: unknown): React.ReactNode {
  if (!value) {
    return <Text color="textSecondary">—</Text>;
  }
  return <Text truncate>{String(value)}</Text>;
}

export function renderCreatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}

export function renderUpdatedAt(value: unknown): React.ReactNode {
  return <Table.DateCell date={new Date(value as string)} />;
}
